import React from "react";
import { RESOURCE_NAME } from "common/authorization";
import { OBJECT_TYPE } from "common/constants";
import {
  useUpdateCarouselSettingsMutation,
  useUpdateCustomMapGeometryCarouselSettingsMutation,
} from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { AuthContextType } from "../../Authorization/types";
import { ActionsProps } from "../../Inputs/DropdownMenu";
import { getDefaultDeleteBody, useDeleteModal } from "../DeleteModal";
import HeaderNav from "../HeaderNav";
import { Body } from "../Typography";
import { CarouselModalWrapper } from "./__styles__/Carousel";
import { Carousel, CarouselDocument } from "./Carousel";
import { CarouselModalActionButton } from "./FullPageCarouselActionButton";

const Counter = ({ index, total }: { index: number; total: number }) => (
  <Body size="default" type="regular" color="contentPrimaryInverted">
    {index + 1} / {total}
  </Body>
);

interface DeleteStreetViewModalHook {
  onSubmit: () => void;
}
export const useDeleteStreetViewModal = ({
  onSubmit,
}: DeleteStreetViewModalHook) => {
  const [show, hide] = useDeleteModal({
    headerText: "Delete Streetview image",
    Content: getDefaultDeleteBody("Streetview image"),
    deleteButtonText: "Delete",
    onDelete: onSubmit,
  });

  return [show, hide] as const;
};

const FullPageCarouselModal = ({
  activeIndex,
  setActiveIndex,
  documents,
  modalClose,
  objectId,
  objectType,
  authContext,
}: {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  documents: CarouselDocument[];
  modalClose: () => void;
  objectId: string;
  objectType: OBJECT_TYPE;
  authContext: AuthContextType;
}) => {
  const [updateSettings] = useUpdateCarouselSettingsMutation();
  const [updateCustomMapGeometryCarouselSettings] =
    useUpdateCustomMapGeometryCarouselSettingsMutation();
  const { addErrorToast, addSuccessToast } = useStatusToasts();

  const canEditSettings = authContext.authorized({
    resource: RESOURCE_NAME.PROPERTY,
    permission: "update",
  });

  const total = documents.length;

  const [show] = useDeleteStreetViewModal({
    onSubmit: async () => {
      await updateSettings({
        variables: {
          id: objectId,
          data: { streetViewEnabled: false },
        },
        onCompleted: () => {
          track("Google streetview image deleted", {
            objectId,
            objectType,
            userId: authContext.user?.id ?? null,
          });
          addSuccessToast("Your Streetview image was deleted");
          if (documents.length > 1) {
            setActiveIndex(0);
          }
          modalClose();
        },
        onError: () => {
          addErrorToast("Failed to delete Streetview image");
        },
      });
    },
  });

  const updateCarouselSettings = async (
    coverImageDocumentUploadId: Maybe<string>,
  ) => {
    const variables = {
      id: objectId,
      data: { coverImageDocumentUploadId },
    };

    const onCompleted = () => {
      track("Cover photo updated", {
        objectId,
        objectType,
        userId: authContext.user?.id ?? null,
      });
      addSuccessToast("Your cover photo was updated");
      setActiveIndex(0);
      modalClose();
    };

    const onError = () => {
      addErrorToast("Failed to update cover photo");
    };

    if (objectType === OBJECT_TYPE.CUSTOM_MAP_GEOMETRY) {
      await updateCustomMapGeometryCarouselSettings({
        variables,
        onCompleted,
        onError,
      });
    } else {
      await updateSettings({ variables, onCompleted, onError });
    }
  };

  const generateActions = (document: CarouselDocument, index: number) => {
    let actions: ActionsProps[] = [];
    const isCoverImage = index === 0;
    if (!isCoverImage) {
      actions.push({
        label: "Make cover photo",
        onClick: async () => {
          await updateCarouselSettings(document.id ?? null);
        },
      });
    }

    const isStreetViewImage = !document.id;
    if (isStreetViewImage) {
      actions.push({
        label: "Delete Streetview image",
        onClick: show,
        variant: "red",
      });
    }

    return actions;
  };

  const documentsWithActions = documents.map((document, index) => {
    const actions = canEditSettings ? generateActions(document, index) : [];
    return {
      ...document,
      actions,
    };
  });

  const middleContainer =
    total > 1 ? <Counter index={activeIndex} total={total} /> : undefined;
  const rightContainer =
    canEditSettings ?
      <CarouselModalActionButton
        actions={documentsWithActions[activeIndex]?.actions ?? []}
      />
    : undefined;

  return (
    <CarouselModalWrapper>
      <HeaderNav
        rightContainer={rightContainer}
        middleContainer={middleContainer}
        onClose={modalClose}
        style={{ position: "static", flex: "none" }}
        dark
      />
      <Carousel
        documents={documents}
        index={activeIndex}
        setIndex={setActiveIndex}
        size={"fullscreen"}
        toggleFullscreenModal={modalClose}
      />
    </CarouselModalWrapper>
  );
};

export default FullPageCarouselModal;
